import {
  triggerToast,
  LaunchPage,
  sortByDateTime,
  PageDS,
  AddOutlined,
  ToastContainerDS,
  usePersistMuiTableState,
  MaterialUiTable,
  MaterialUiColumnsProps,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  rulesSlice,
  rulesListAsync,
  selectRuleStatus,
  selectRulesList,
  selectRulesListStatus,
  selectUpdatedRuleStatus,
  selectRuleToDeleteStatus,
} from "./rulesSlice";
import { PublicRuleType, RulesListDisplayedType } from "./rulesAPI";
import { useNavigate, useParams } from "react-router-dom";
import { selectCompany, selectManager } from "../homeSlice";
import "../../../i18n/i18n";
import { selectManagersList } from "../users/usersSlice";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { useMenuOptionsRules } from "./menuOptionsRules";
import { DeleteRuleModal } from "./deleteRuleModal";

export const Rules = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const createRuleStatus = useAppSelector(selectRuleStatus);
  const updateRuleStatus = useAppSelector(selectUpdatedRuleStatus);
  const rulesListInit = useAppSelector(selectRulesList);
  const rulesListStatus = useAppSelector(selectRulesListStatus);
  const company = useAppSelector(selectCompany);
  const [ruleName, setRuleName] = useState("");
  const [isModalCreationVisible, setIsModalCreationVisible] = useState(false);
  const manager = useAppSelector(selectManager);
  const managers = useAppSelector(selectManagersList);
  const [ruleToDelete, setRuleToDelete] = useState<PublicRuleType | null>(null);
  const menuOptionsRules = useMenuOptionsRules({
    currentManager: manager,
    setRuleToDelete: setRuleToDelete,
  });
  const ruleToDeleteStatus = useAppSelector(selectRuleToDeleteStatus);

  const pageName = "rules";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      name: "size",
      vehicleCount: "avg",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  const [listDisplayed, setListDisplayed] = useState<
    RulesListDisplayedType[] | null
  >(null);

  const rulesList: PublicRuleType[] = useMemo(() => {
    const rowsWithId = rulesListInit.map((rule) => {
      const newDict: PublicRuleType & { id: string } = {
        ...rule,
        id: rule.uuid,
      };
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc");
  }, [rulesListInit]);

  const isUserManager = useMemo(
    () => manager && manager.role === "USER",
    [manager],
  );

  const routerParams = useParams();

  useEffect(() => {
    if (routerParams.name) {
      setRuleName(routerParams.name);
    }
  }, [routerParams.name]);

  useEffect(() => {
    if (isModalCreationVisible) {
      navigate("/home/rule_creation/rule");
    }
  }, [isModalCreationVisible, navigate]);

  useEffect(() => {
    if (company && rulesListStatus === "idle") {
      void dispatch(rulesListAsync(company.uuid));
    }
  }, [dispatch, company, rulesListStatus]);

  useEffect(() => {
    if (createRuleStatus === "success" && ruleName !== "") {
      triggerToast(t("rules.createRule.success") + ruleName || "", "valid");
      dispatch(rulesSlice.actions.resetRuleCreationStatus());
    } else if (createRuleStatus === "failed") {
      triggerToast(t("rules.createRule.failure") || "", "error");
      dispatch(rulesSlice.actions.resetRuleCreationStatus());
    }
  }, [createRuleStatus, dispatch, ruleName, t, updateRuleStatus]);

  useEffect(() => {
    if (updateRuleStatus === "success" && ruleName !== "") {
      triggerToast(t("rules.update.success") + ruleName || "", "valid");
      dispatch(rulesSlice.actions.resetRuleUpdateStatus());
    } else if (updateRuleStatus === "failed") {
      triggerToast(t("rules.update.failure") || "", "error");
      dispatch(rulesSlice.actions.resetRuleUpdateStatus());
    }
  }, [updateRuleStatus, t, createRuleStatus, ruleName, dispatch]);

  useEffect(() => {
    if (rulesList && rulesListStatus === "success") {
      const rulesDateTimeSorted = sortByDateTime(rulesList, "desc");
      setListDisplayed(rulesDateTimeSorted);
    }
  }, [dispatch, rulesList, rulesListStatus]);

  useEffect(() => {
    if (ruleToDeleteStatus === "success") {
      triggerToast(t("rules.delete.success") || "", "valid");
    } else if (ruleToDeleteStatus === "failed") {
      triggerToast(t("rules.delete.failure") || "", "error");
    }
  }, [ruleToDeleteStatus, t]);

  const retrieveRuleUuid = useCallback(
    (ruleName: string) => {
      const rule = rulesList.find((rule) => rule.name === ruleName);
      if (!rule) {
        triggerToast(t("rules.reloadRule.error") || "", "error");
        return;
      }
      void dispatch(rulesSlice.actions.resetRulesList());
      navigate(`/home/rule/${rule.uuid}`);
    },
    [dispatch, navigate, rulesList, t],
  );

  const columnsDisplayed: {
    [index in keyof RulesListDisplayedType]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 500,
      },
      vehicleCount: {
        type: "number",
      },
    };
  }, []);

  const cta = {
    title: t("rules.cta"),
    action: () => {
      setIsModalCreationVisible(true);
    },
    leftIcon: <AddOutlined />,
  };

  const searchBarProps = {
    values: rulesList,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const, "date" as const],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("rules.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <PageDS
      title={t("rules.title")}
      cta={isUserManager ? undefined : cta}
      toaster={<ToastContainerDS />}
      supportButton={{
        showMail: !otherSupportPhones,
        otherPhones: otherSupportPhones,
      }}
    >
      <MaterialUiTable<keyof RulesListDisplayedType, RulesListDisplayedType>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        onRowClickAction={(props) => retrieveRuleUuid(props.name)}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
        menuOptions={
          menuOptionsRules && menuOptionsRules.length > 0
            ? { options: menuOptionsRules, hasBorderRow: true }
            : undefined
        }
      />
      {ruleToDelete ? (
        <DeleteRuleModal
          isVisible={!!ruleToDelete}
          onCloseModal={() => setRuleToDelete(null)}
          rule={ruleToDelete}
        />
      ) : (
        <></>
      )}
    </PageDS>
  );
};
